"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import type {
  InsightsResponse,
  MealInstance,
  PlanProposalRequest,
  PlanResponse,
  ProductStateResponse,
  Profile,
} from "@bitebalance/contracts"
import { useAmyDataRefresh } from "@/lib/useAmyDataRefresh"
import { useApiClient } from "@/api/useApiClient"
import { useI18n } from "@/i18n"
import { LoadingState, EmptyState, ErrorState } from "@/components/state"
import { MealCard } from "@/components/plan/MealCard"
import { DailyTargetBar } from "@/components/plan/DailyTargetBar"
import {
  indexOutcomeFacts,
  mealOutcomeExtras,
  outcomeFactsFromTodayLedger,
} from "@/components/plan/outcomeDisplay"
import { LedgerStrip } from "@/components/plan/LedgerStrip"
import { BackfillCard } from "@/components/plan/BackfillCard"
import type { DayLedgerResponse } from "@bitebalance/contracts"
import { ValueCard } from "@/components/plan/ValueCard"
import { SwapProposalSheet } from "@/components/plan/SwapProposalSheet"
import { OutcomeSheet } from "@/components/plan/OutcomeSheet"
import { RegenFailedBanner } from "@/components/plan/RegenFailedBanner"
import { WeekEndedBanner } from "@/components/plan/WeekEndedBanner"
import { planCoversDate } from "@/lib/planWeek"
import { ConstraintConflictBanner } from "@/components/plan/ConstraintConflictBanner"
import { ClarificationLine } from "@/components/plan/ClarificationLine"
import { BudgetTodayLine } from "@/components/plan/BudgetTodayLine"
import { BudgetTransitionNotice } from "@/components/budget/BudgetTransitionNotice"
import { fetchBudgetEconomics } from "@/lib/productState"
import { reasonBadgeLabel } from "@/components/plan/recipeDisplay"
import { dayPhaseClass } from "@/components/plan/SlotGlyph"

type LoadStatus = "loading" | "success" | "error"

const SLOT_ORDER: Record<MealInstance["slot"], number> = {
  breakfast: 0,
  morning_snack: 1,
  lunch: 2,
  afternoon_snack: 3,
  dinner: 4,
  snack: 5,
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function greetingKey(): "greetingMorning" | "greetingAfternoon" | "greetingEvening" {
  const hour = new Date().getHours()
  if (hour < 12) return "greetingMorning"
  if (hour < 18) return "greetingAfternoon"
  return "greetingEvening"
}

/** Today (T12): date/greeting, meal cards, daily target bar, and the value card. */
export default function TodayPage() {
  const { t, locale } = useI18n()
  const api = useApiClient()
  const [status, setStatus] = useState<LoadStatus>("loading")
  const [plan, setPlan] = useState<PlanResponse | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [insights, setInsights] = useState<InsightsResponse | null>(null)
  // Budget Model v2: the compact goal line reads the backend-owned truth
  // state; a failed fetch simply hides the line (never a guess).
  // Three states (owner invariant 5, 2026-09-04): `undefined` = still
  // loading (no budget notice or failure banner may render yet — no stale
  // banner flash before the transition notice can be decided), `null` =
  // the read failed (profile fallback applies), object = resolved.
  const [budgetEconomics, setBudgetEconomics] = useState<
    ProductStateResponse["budgetEconomics"] | null | undefined
  >(undefined)

  const [proposalMeal, setProposalMeal] = useState<MealInstance | null>(null)
  const [proposalIntent, setProposalIntent] = useState<PlanProposalRequest["intent"]>("swap")
  const [outcomeMeal, setOutcomeMeal] = useState<MealInstance | null>(null)
  // Owner incident 2026-08-30: cards must show PERSISTED outcomes — marks
  // looked lost on every reload and got re-recorded. Ledger is the truth.
  const [ledger, setLedger] = useState<DayLedgerResponse | null>(null)
  const [ledgerNudge, setLedgerNudge] = useState(0)
  useEffect(() => {
    let cancelled = false
    api
      .getDayLedger()
      .then(l => {
        if (!cancelled) setLedger(l)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [api, ledgerNudge])
  // ADR-056: marks are the ledger's canonical consumption records, looked
  // up by the card's (date, slot) through the ONE builder Plan uses too —
  // never by a wire meal id, which the next plan revision re-issues.
  const outcomeIndex = useMemo(
    () => indexOutcomeFacts(outcomeFactsFromTodayLedger(ledger)),
    [ledger]
  )

  const load = useCallback(
    (quiet = false) => {
      if (!quiet) {
        setStatus("loading")
        // A full reload re-arms the loading gate; a quiet refresh keeps the
        // last resolved budget state on screen (no flash either way).
        setBudgetEconomics(undefined)
      }
      Promise.all([api.getCurrentPlan(), api.getProfile(), api.getInsightsValue()])
        .then(([planRes, profileRes, insightsRes]) => {
          setPlan(planRes)
          // See ConstraintsPage.tsx's note: client.ts types getProfile() as
          // `Profile` although the wire response is `ProfileResponse`; both
          // share the fields this page reads.
          setProfile(profileRes as unknown as Profile)
          setInsights(insightsRes)
          setStatus("success")
        })
        .catch(() => {
          // A quiet refresh keeps the last good view — never downgrade a
          // rendered dashboard to an error screen behind the chat sheet.
          if (!quiet) setStatus("error")
        })
      void fetchBudgetEconomics(api).then(setBudgetEconomics)
    },
    [api]
  )

  useEffect(() => {
    load()
  }, [load])

  // ADR-036 finding 6: when Amy rebuilds the plan / changes the target /
  // logs food, this view refreshes in place instead of going stale.
  useAmyDataRefresh(() => {
    load(true)
    // The consumed bar must move when Amy logs food / records outcomes.
    setLedgerNudge(n => n + 1)
  })

  const today = todayIso()
  const todaysMeals = useMemo(() => {
    if (!plan) return []
    return plan.meals
      .filter(m => m.date === today)
      .sort((a, b) => SLOT_ORDER[a.slot] - SLOT_ORDER[b.slot])
  }, [plan, today])

  const todaysTotals = useMemo(
    () =>
      todaysMeals.reduce(
        (acc, m) => ({
          kcal: acc.kcal + m.nutrition.kcal,
          proteinG: acc.proteinG + m.nutrition.proteinG,
          carbsG: acc.carbsG + m.nutrition.carbsG,
          fatG: acc.fatG + m.nutrition.fatG,
        }),
        { kcal: 0, proteinG: 0, carbsG: 0, fatG: 0 }
      ),
    [todaysMeals]
  )

  const subtitle = useMemo(() => {
    const counts = new Map<string, number>()
    for (const m of todaysMeals) {
      for (const code of m.reasonCodes) counts.set(code, (counts.get(code) ?? 0) + 1)
    }
    let topCode: string | null = null
    let topCount = 0
    for (const [code, count] of counts) {
      if (count > topCount) {
        topCode = code
        topCount = count
      }
    }
    if (!topCode) return t("views.today.subtitleDefault")
    return t("views.today.subtitleReason", { reason: reasonBadgeLabel(topCode, t) })
  }, [todaysMeals, t])

  function openProposal(meal: MealInstance, intent: PlanProposalRequest["intent"]) {
    setProposalMeal(meal)
    setProposalIntent(intent)
  }

  if (status === "loading") return <LoadingState />
  if (status === "error") return <ErrorState onRetry={load} />
  if (!plan || !profile) return <ErrorState onRetry={load} />

  // Budget Model v2 migration (redesign 2026-09-04): while the account still
  // carries an UNCLASSIFIED pre-v2 amount, Today explains the transition and
  // leads to the setup card — it never shows the "under your maximum"
  // failure/goal line for a maximum the user never chose. Server-driven:
  // product state first, the profile as the fallback when that read failed.
  // Loading gate (owner invariant 5): until the product-state read has
  // RESOLVED (success or failure) nothing budget-related — transition
  // notice, goal line, or a failed-rebuild banner — may render.
  const budgetStateResolved = budgetEconomics !== undefined
  const unclassifiedBudget =
    budgetStateResolved &&
    (budgetEconomics?.budgetModel === "legacy_hard_cap" ||
      (budgetEconomics === null && profile.budget?.model === "legacy_hard_cap"))
  const previousBudgetCents =
    budgetEconomics?.legacyWeeklyLimitCents ?? profile.budget?.legacyWeeklyLimitEurCents ?? null
  const failedJob = plan.latestJob?.status === "failed" ? plan.latestJob : null
  // The unclassified transition notice outranks any BUDGET_INFEASIBLE
  // presentation; otherwise the banner's own branch is driven by the
  // server-derived `latestJob.applicability` (current vs superseded).
  const showRegenFailed =
    budgetStateResolved &&
    failedJob !== null &&
    !(unclassifiedBudget && failedJob.failedError?.code === "BUDGET_INFEASIBLE")

  // P1 (founder-reported, 2026-09-22): `plan` is non-null here (the early
  // return above already handled the genuinely-no-plan-ever case) — a real
  // plan/revision that simply no longer covers today's date is the ONLY
  // other reason `todaysMeals` can be empty in practice (a plan's own week
  // is always fully populated by construction). Distinguishes the new
  // "week ended" state from the pre-existing generic empty state below.
  const weekEnded = !planCoversDate(plan.weekStartDate, today)

  const dateLabel = new Intl.DateTimeFormat(locale === "de" ? "de-DE" : "en-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date())

  return (
    /* T21 (ADR-070): the surface gets a ground — a dawn / noon / dusk wash chosen
       by the clock, which says nothing about the food — and the day column and its
       context sit side by side at desktop instead of one stretched phone column. */
    <section aria-labelledby="today-heading" className={`flex flex-col gap-7 ${dayPhaseClass()}`}>
      {/* Editorial header (2026-08 UI pass): the landing's Fraunces voice,
          finally inside the app — small-caps date eyebrow, serif greeting. */}
      <div className="anim-rise">
        <p className="text-xs font-bold tracking-[0.14em] text-green uppercase">{dateLabel}</p>
        <h1
          id="today-heading"
          className="font-display mt-1.5 text-4xl leading-tight font-bold tracking-tight text-ink md:text-5xl"
        >
          {t(`views.today.${greetingKey()}`)}
        </h1>
        <p className="mt-2 max-w-prose text-sm text-ink-2">{subtitle}</p>
        {budgetStateResolved && !unclassifiedBudget && (
          <div className="mt-2">
            <BudgetTodayLine budgetEconomics={budgetEconomics ?? null} />
          </div>
        )}
      </div>

      {unclassifiedBudget && <BudgetTransitionNotice amountCents={previousBudgetCents} />}
      <ClarificationLine />

      {plan.constraintConflicts && plan.constraintConflicts.length > 0 && (
        <ConstraintConflictBanner
          planId={plan.id}
          baseRevision={plan.revision}
          conflicts={plan.constraintConflicts}
          onApplied={load}
        />
      )}
      {showRegenFailed && failedJob && (
        <RegenFailedBanner
          onRegenerated={load}
          failedError={failedJob.failedError}
          applicability={failedJob.applicability}
        />
      )}

      <div className="anim-rise anim-rise-1">
        <DailyTargetBar
          totals={todaysTotals}
          calorieTarget={profile.calorieTarget}
          toleranceCaloriesPercent={profile.macroTargets.toleranceCaloriesPercent}
          ledger={ledger}
        />
        <LedgerStrip />
      </div>

      <BackfillCard onLogged={() => setLedgerNudge(n => n + 1)} />

      {todaysMeals.length === 0 ? (
        weekEnded ? (
          // P1 (founder-reported, 2026-09-22): a real plan whose week has
          // ENDED is a distinct, truthful state from "nothing scheduled".
          <WeekEndedBanner onRegenerated={load} />
        ) : (
          <EmptyState
            title={t("views.today.noMealsTitle")}
            body={t("views.today.noMealsBody")}
            action={
              <Link to="/app/plan" className="text-sm font-medium text-green underline">
                {t("views.today.viewPlanCta")}
              </Link>
            }
          />
        )
      ) : (
        <div className="anim-rise anim-rise-2 flex flex-col gap-3">
          {todaysMeals.map(meal => {
            const extras = mealOutcomeExtras(outcomeIndex, meal, t, locale)
            return (
              <MealCard
                key={meal.id}
                meal={meal}
                loggedOutcome={extras?.outcome ?? null}
                outcomeDetail={extras?.detail ?? null}
                outcomeReplacement={extras?.replacement ?? null}
                onSwap={m => openProposal(m, "swap")}
                onMarkOutcome={m => setOutcomeMeal(m)}
                constraintConflict={(plan.constraintConflicts ?? []).some(
                  c => c.mealId === meal.id
                )}
              />
            )
          })}
        </div>
      )}

      <div className="anim-rise anim-rise-3">
        <ValueCard insights={insights} />
      </div>

      <SwapProposalSheet
        open={proposalMeal !== null}
        onOpenChange={open => {
          if (!open) setProposalMeal(null)
        }}
        planId={plan.id}
        baseRevision={plan.revision}
        meal={proposalMeal}
        intent={proposalIntent}
        onApplied={result => setPlan(result.plan)}
        onStaleRevision={() => {
          setProposalMeal(null)
          load()
        }}
      />

      <OutcomeSheet
        open={outcomeMeal !== null}
        onOpenChange={open => {
          if (!open) {
            setOutcomeMeal(null)
            // Re-read the ledger so a just-recorded outcome shows on its card.
            setLedgerNudge(n => n + 1)
          }
        }}
        meal={outcomeMeal}
      />
    </section>
  )
}
